import styled, { keyframes } from 'styled-components';

// ===== CORES =====
const colors = {
  background: '#0D0D0D',
  backgroundAlt: '#111111',
  backgroundCard: '#161616',
  primary: '#00A8E8',
  primaryDark: '#0077B6',
  primaryLight: '#48CAE4',
  success: '#2ECC71',
  white: '#FFFFFF',
  textPrimary: '#F5F5F5',
  textSecondary: '#B8B8B8',
  textMuted: '#888888',
  border: 'rgba(255, 255, 255, 0.08)',
  borderHover: 'rgba(0, 168, 232, 0.3)',
  gridColor: 'rgba(0, 168, 232, 0.04)'
};

// ===== BREAKPOINTS =====
const breakpoints = {
  mobile: '576px',
  tablet: '768px',
  laptop: '1024px',
  desktop: '1280px',
  large: '1600px'
};

// ===== ANIMAÇÕES =====
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-8px) rotate(-2deg); }
`;

// ===== PAGE WRAPPER =====
export const PageWrapper = styled.div`
  min-height: 100vh;
  background: ${colors.background};
  position: relative;
  overflow-x: hidden;
`;

export const GridBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: 
    linear-gradient(${colors.gridColor} 1px, transparent 1px),
    linear-gradient(90deg, ${colors.gridColor} 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
`;

// ===== HERO SECTION =====
export const HeroSection = styled.section`
  position: relative;
  z-index: 2;
  padding: calc(72px + 1rem) 2rem 4rem;
  display: flex;
  align-items: center;
  overflow: visible;
  
  @media (min-width: ${breakpoints.large}) {
    padding: calc(80px + 1.5rem) 4rem 5rem;
  }
  
  @media (max-width: ${breakpoints.laptop}) {
    padding: calc(64px + 1rem) 1.5rem 3rem;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: calc(56px + 0.5rem) 1rem 2.5rem;
  }
`;

export const HeroContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 2rem;
  align-items: center;
  
  > div:first-child {
    padding-top: 2rem;
  }
  
  @media (min-width: ${breakpoints.large}) {
    max-width: 1100px;
    gap: 3rem;
    
    > div:first-child {
      padding-top: 2.5rem;
    }
  }
  
  @media (max-width: ${breakpoints.laptop}) {
    max-width: 850px;
    gap: 1.5rem;
    
    > div:first-child {
      padding-top: 1.5rem;
    }
  }
  
  @media (max-width: ${breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
    max-width: 500px;
    
    > div:first-child {
      padding-top: 0;
    }
  }
`;

export const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: rgba(0, 168, 232, 0.1);
  border: 1px solid rgba(0, 168, 232, 0.25);
  padding: 0.4rem 0.9rem;
  border-radius: 50px;
  font-size: 0.7rem;
  font-weight: 700;
  color: ${colors.primary};
  width: fit-content;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 1.25rem;
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.6s ease forwards; }
  }
  
  @media (max-width: ${breakpoints.tablet}) {
    margin: 0 auto 1.25rem;
  }
`;

export const HeroTitle = styled.h1`
  font-size: clamp(1.6rem, 3.5vw, 2.75rem);
  font-weight: 900;
  line-height: 1.15;
  color: ${colors.textPrimary};
  letter-spacing: -0.03em;
  margin-bottom: 1.25rem;
  
  .highlight {
    color: ${colors.primary};
    display: block;
  }
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.6s ease 0.1s forwards; }
  }
  
  @media (max-width: ${breakpoints.laptop}) {
    font-size: clamp(1.4rem, 4vw, 2rem);
  }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(0.85rem, 1.1vw, 1rem);
  color: ${colors.textMuted};
  line-height: 1.6;
  max-width: 400px;
  margin-bottom: 1.75rem;
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.6s ease 0.2s forwards; }
  }
  
  @media (max-width: ${breakpoints.tablet}) {
    max-width: 100%;
    margin: 0 auto 1.75rem;
  }
`;

export const HeroCTA = styled.div`
  display: flex;
  gap: 0.75rem;
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.6s ease 0.3s forwards; }
  }
  
  @media (max-width: ${breakpoints.tablet}) {
    justify-content: center;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    width: 100%;
  }
`;

export const HeroButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: ${colors.primary};
  color: ${colors.white};
  text-decoration: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  border: 2px solid ${colors.primary};
  
  &:hover {
    background: ${colors.primaryDark};
    border-color: ${colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 168, 232, 0.35);
  }
  
  svg { font-size: 1rem; }
`;

export const HeroButtonSecondary = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: ${colors.textSecondary};
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.3s ease;
  border: 2px solid ${colors.border};
  
  &:hover {
    border-color: ${colors.primary};
    color: ${colors.primary};
    svg { transform: translateX(3px); }
  }
  
  svg {
    font-size: 0.75rem;
    transition: transform 0.3s ease;
  }
`;

export const HeroImageStack = styled.div`
  position: relative;
  width: 420px;
  height: 320px;
  flex-shrink: 0;
  z-index: 10;
  margin-top: -30px;
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.8s ease 0.2s forwards; }
  }
  
  @media (min-width: ${breakpoints.large}) {
    width: 480px;
    height: 360px;
    margin-top: -20px;
  }
  
  @media (max-width: ${breakpoints.laptop}) {
    width: 380px;
    height: 285px;
    margin-top: -15px;
  }
  
  @media (max-width: ${breakpoints.tablet}) {
    width: 340px;
    height: 255px;
    margin: 0 auto;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    width: 280px;
    height: 210px;
  }
`;

export const HeroImageCard = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  transition: opacity 0.8s ease-in-out, filter 0.3s ease;
  cursor: pointer;
  opacity: 0;
  filter: drop-shadow(0 0 20px rgba(0, 168, 232, 0.4))
          drop-shadow(0 0 40px rgba(0, 168, 232, 0.2));
  
  &:hover {
    filter: drop-shadow(0 0 25px rgba(0, 168, 232, 0.5))
            drop-shadow(0 0 50px rgba(0, 168, 232, 0.3));
  }
  
  &[data-position="0"] {
    opacity: 1;
  }
`;

export const HeroVideoCard = styled.video`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: auto;
  transition: opacity 0.8s ease-in-out, filter 0.3s ease;
  cursor: pointer;
  opacity: 0;
  filter: drop-shadow(0 0 20px rgba(0, 168, 232, 0.4))
          drop-shadow(0 0 40px rgba(0, 168, 232, 0.2));
  border-radius: 8px;
  
  &:hover {
    filter: drop-shadow(0 0 25px rgba(0, 168, 232, 0.5))
            drop-shadow(0 0 50px rgba(0, 168, 232, 0.3));
  }
  
  &[data-position="0"] {
    opacity: 1;
  }
`;

export const HeroImageTooltip = styled.div`
  position: absolute;
  bottom: -120px;
  right: -30px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  opacity: 1;
  visibility: visible;
  transition: all 0.3s ease;
  pointer-events: none;
  
  &::before {
    content: '';
    width: 20px;
    height: 1px;
    background: rgba(0, 168, 232, 0.4);
  }
  
  span {
    font-size: 0.7rem;
    color: ${colors.textMuted};
    font-weight: 400;
    letter-spacing: 0.02em;
  }
  
  @media (max-width: ${breakpoints.tablet}) {
    display: none;
  }
`;

export const HeroImageWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: opacity 0.8s ease-in-out;
  
  &:hover ${HeroImageTooltip} {
    opacity: 1;
    visibility: visible;
  }
`;

// ===== DEPOIMENTOS / TESTIMONIALS =====
export const TestimonialsSection = styled.section`
  position: relative;
  z-index: 1;
  padding: 3rem 1.5rem;
  background: ${colors.backgroundAlt};
  border-top: 1px solid ${colors.border};
  border-bottom: 1px solid ${colors.border};
  overflow: hidden;
  
  @media (max-width: ${breakpoints.laptop}) {
    padding: 2.5rem 1.25rem;
  }
`;

export const TestimonialsHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.6s ease forwards; }
  }
`;

export const TestimonialsTitle = styled.h2`
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 900;
  color: ${colors.textPrimary};
  line-height: 1.3;
  
  span {
    color: ${colors.primary};
  }
`;

export const TestimonialsTrack = styled.div`
  overflow: hidden;
  width: 100%;
  max-width: 1020px;
  margin: 0 auto;
  
  @media (max-width: ${breakpoints.tablet}) {
    max-width: 300px;
  }
`;

export const TestimonialsCarousel = styled.div`
  display: flex;
  gap: 1.25rem;
  transition: transform 0.5s ease;
  transform: translateX(${props => props.$offset}%);
  padding: 0.5rem 0;
  
  @media (max-width: ${breakpoints.tablet}) {
    gap: 0;
    transform: translateX(${props => props.$mobileOffset}%);
  }
`;

export const CarouselControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

export const CarouselDot = styled.button`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: ${props => props.$active ? colors.primary : colors.border};
  cursor: pointer;
  transition: all 0.3s ease;
  padding: 0;
  
  &:hover {
    background: ${props => props.$active ? colors.primary : colors.primaryLight};
  }
`;

export const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid ${colors.border};
  background: ${colors.backgroundCard};
  color: ${colors.textSecondary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  
  &:hover {
    border-color: ${colors.primary};
    color: ${colors.primary};
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  &.prev {
    left: 0;
  }
  
  &.next {
    right: 0;
  }
  
  @media (max-width: ${breakpoints.laptop}) {
    display: none;
  }
`;

export const TestimonialsWrapper = styled.div`
  position: relative;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 50px;
  
  @media (max-width: ${breakpoints.laptop}) {
    padding: 0 1rem;
  }
`;

export const TestimonialCard = styled.div`
  flex: 0 0 calc((100% - 2.5rem) / 3);
  min-width: 300px;
  background: ${colors.backgroundCard};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  padding: 1.5rem;
  position: relative;
  transition: all 0.3s ease;
  
  &::before {
    content: '"';
    position: absolute;
    top: -5px;
    left: 1.25rem;
    font-size: 3.5rem;
    font-weight: 900;
    color: ${colors.primary};
    opacity: 0.3;
    line-height: 1;
  }
  
  &:hover {
    border-color: ${colors.borderHover};
  }
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.5s ease forwards; }
  }
  
  @media (max-width: ${breakpoints.tablet}) {
    flex: 0 0 100%;
    min-width: 100%;
  }
`;

export const TestimonialTextWrapper = styled.div`
  position: relative;
  max-height: ${props => props.$expanded ? '500px' : '100px'};
  overflow: hidden;
  transition: max-height 0.4s ease;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 30px;
    background: ${props => props.$expanded ? 'transparent' : `linear-gradient(transparent, ${colors.backgroundCard})`};
    pointer-events: none;
    transition: background 0.3s ease;
  }
`;

export const ReadMoreButton = styled.button`
  background: none;
  border: none;
  color: ${colors.primary};
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 0 0 0;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  transition: all 0.2s ease;
  
  &:hover {
    color: ${colors.primaryLight};
  }
  
  svg {
    font-size: 0.7rem;
    transition: transform 0.3s ease;
    transform: ${props => props.$expanded ? 'rotate(180deg)' : 'rotate(0)'};
  }
`;

// ===== NOVOS COMPONENTES DE TESTIMONIALS (layout Google) =====
export const TestimonialHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

export const AuthorPhoto = styled.img`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
`;

export const AuthorAvatar = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark});
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
`;

export const AuthorInfo = styled.div`
  flex: 1;
`;

export const AuthorName = styled.div`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 0.25rem;
`;

export const TestimonialMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export const TestimonialStars = styled.div`
  display: flex;
  gap: 0.1rem;
  
  svg {
    color: #F5A623;
    font-size: 0.85rem;
  }
`;

export const TimeAgo = styled.span`
  font-size: 0.75rem;
  color: ${colors.textMuted};
`;

export const TestimonialText = styled.p`
  font-size: 0.9rem;
  color: ${colors.textSecondary};
  line-height: 1.6;
  margin: 0;
`;

// Mantidos para compatibilidade
export const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-top: 1rem;
  border-top: 1px solid ${colors.border};
`;

export const AuthorRole = styled.div`
  font-size: 0.7rem;
  color: ${colors.textMuted};
  line-height: 1.3;
`;

// ===== STATS ROW =====
export const StatsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid ${colors.border};
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.6s ease 0.2s forwards; }
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    gap: 1.5rem;
  }
`;

export const StatItem = styled.div`
  text-align: center;
`;

export const StatNumber = styled.div`
  font-size: clamp(1.5rem, 2.5vw, 2rem);
  font-weight: 900;
  color: ${colors.primary};
  line-height: 1;
  margin-bottom: 0.2rem;
`;

export const StatLabel = styled.div`
  font-size: 0.7rem;
  color: ${colors.textMuted};
  font-weight: 500;
`;

// ===== SERVIÇOS =====
export const ServicesSection = styled.section`
  position: relative;
  z-index: 1;
  padding: 4rem 1.5rem 3rem;
  background: ${colors.background};
  
  @media (min-width: ${breakpoints.large}) {
    padding: 4.5rem 1.5rem 3rem;
  }
  
  @media (max-width: ${breakpoints.laptop}) {
    padding: 3.5rem 1.25rem 2.5rem;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    padding: 3rem 1rem 2rem;
  }
`;

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.6s ease forwards; }
  }
`;

export const SectionTitle = styled.h2`
  font-size: clamp(1.35rem, 2.5vw, 1.85rem);
  font-weight: 900;
  color: ${colors.textPrimary};
  margin-bottom: 0.5rem;
`;

export const SectionSubtitle = styled.p`
  font-size: 0.9rem;
  color: ${colors.textMuted};
`;

export const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: ${breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
    max-width: 650px;
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    grid-template-columns: 1fr;
    max-width: 350px;
  }
`;

export const ServiceCard = styled.div`
  background: ${colors.backgroundCard};
  border: 1px solid ${colors.border};
  border-radius: 12px;
  padding: 1.25rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: ${colors.borderHover};
    transform: translateY(-4px);
  }
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.5s ease forwards; }
  }
`;

export const ServiceIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.primaryDark});
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  font-size: 1rem;
  margin-bottom: 0.85rem;
`;

export const ServiceTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 700;
  color: ${colors.textPrimary};
  margin-bottom: 0.65rem;
`;

export const ServiceList = styled.ul`
  list-style: none;
  
  li {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.75rem;
    color: ${colors.textSecondary};
    margin-bottom: 0.35rem;
    
    svg {
      color: ${colors.success};
      font-size: 0.6rem;
      flex-shrink: 0;
    }
  }
`;

// ===== CTA SECTION =====
export const CTASection = styled.section`
  position: relative;
  z-index: 1;
  padding: 3rem 1.5rem;
  background: linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.background} 100%);
  
  @media (max-width: ${breakpoints.laptop}) {
    padding: 2.5rem 1.25rem;
  }
`;

export const CTAContent = styled.div`
  max-width: 550px;
  margin: 0 auto;
  text-align: center;
  
  &.animate-on-scroll {
    opacity: 0;
    &.visible { animation: ${fadeInUp} 0.6s ease forwards; }
  }
`;

export const CTATitle = styled.h2`
  font-size: clamp(1.25rem, 2.5vw, 1.75rem);
  font-weight: 900;
  color: ${colors.white};
  margin-bottom: 0.5rem;
`;

export const CTASubtitle = styled.p`
  font-size: 0.9rem;
  color: ${colors.textSecondary};
  margin-bottom: 1.5rem;
`;

export const CTAButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 1.5rem;
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
  }
`;

export const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  background: ${colors.primary};
  color: ${colors.white};
  text-decoration: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${colors.primaryDark};
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 168, 232, 0.4);
  }
  
  svg { font-size: 1.1rem; }
`;

export const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.85rem 1.75rem;
  background: transparent;
  color: ${colors.white};
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  border: 2px solid rgba(255,255,255,0.3);
  
  &:hover {
    border-color: ${colors.white};
    background: rgba(255,255,255,0.1);
  }
  
  svg { font-size: 1rem; }
`;

export const ContactRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  font-size: 0.8rem;
  color: ${colors.textMuted};
  
  span {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    
    svg {
      color: ${colors.primary};
    }
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    flex-direction: column;
    gap: 0.5rem;
  }
`;

// ===== WHATSAPP FLUTUANTE =====
export const WhatsAppFloat = styled.a`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  width: 56px;
  height: 56px;
  background: #25D366;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.white};
  font-size: 1.75rem;
  box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
  transition: all 0.3s ease;
  z-index: 999;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(37, 211, 102, 0.5);
  }
  
  @media (max-width: ${breakpoints.mobile}) {
    bottom: 1rem;
    right: 1rem;
    width: 50px;
    height: 50px;
    font-size: 1.5rem;
  }
`;